import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { AddOn, ConfiguredAddOn } from '../../../types/addon.types';
import type { ConfiguredService } from '../../../types/service.types';
import {
    configureAddOnsSchema,
    type ConfigureAddOnsFormData,
} from '../../../schemas/ConfigureAddOnsSchema';
import { scheduleOptionsFor } from '../../../utils/addOnInstances';
import { useSelectedLinkReset } from '../../../hooks';
import { Badge } from '@shared/components';
import { isPricedLine } from '../../../utils/pricedLine';
import { formatCurrency } from '@/app/utils/formatCurrency';
import { AddOnScheduleForm } from './AddOnScheduleForm';
import { AddOnCardActions } from './AddOnCardActions';
import AddOnItem from '../AddOnItem';

interface AddOnCardProps {
    addOn: AddOn;
    instances: ConfiguredAddOn[];
    services: ConfiguredService[];
    currency?: string;
    onAdd: (addOnId: string, schedule: string, date: Date | null) => void;
    onRemove: (instanceId: string | number) => void;
}

export function AddOnCard({
    addOn,
    instances,
    services,
    currency = 'GBP',
    onAdd,
    onRemove,
}: AddOnCardProps) {
    const headingId = useId();
    const formId = useId();
    const [isAdding, setIsAdding] = useState(false);
    const schema = useMemo(() => configureAddOnsSchema(services.length), [services.length]);
    const resolver = useMemo(() => zodResolver(schema), [schema]);
    const { control, handleSubmit, setValue, reset, trigger } = useForm<ConfigureAddOnsFormData>({
        defaultValues: { linkedServiceId: 'independent', addOnDate: null },
        mode: 'onChange',
        resolver,
    });
    const values = useWatch({ control });
    const linkedServiceId = values.linkedServiceId ?? 'independent';
    const canConfirm = useMemo(() => schema.safeParse(values).success, [schema, values]);
    const options = useMemo(
        () => scheduleOptionsFor(addOn.id, services, instances),
        [addOn.id, services, instances]
    );
    const resetToIndependent = useCallback(
        () => setValue('linkedServiceId', 'independent'),
        [setValue]
    );

    useSelectedLinkReset(linkedServiceId, options, resetToIndependent);

    useEffect(() => {
        if (isAdding) void trigger();
    }, [isAdding, trigger]);

    const closeForm = useCallback(() => {
        reset();
        setIsAdding(false);
    }, [reset]);

    const confirm = handleSubmit((data) => {
        onAdd(addOn.id, data.linkedServiceId, data.addOnDate ?? null);
        closeForm();
    });

    return (
        <section
            aria-labelledby={headingId}
            className="rounded-lg border border-mp-border bg-mp-surface p-4"
        >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                <div>
                    <div className="flex flex-wrap items-center gap-2">
                        <h3 id={headingId} className="font-semibold text-mp-foreground">
                            {addOn.name}
                            {instances.length > 0 && (
                                <span className="ml-2 text-xs font-normal text-mp-muted">
                                    {instances.length} added
                                </span>
                            )}
                        </h3>
                        {addOn.category && (
                            <Badge variant="info">
                                <span className="capitalize">{addOn.category}</span>
                            </Badge>
                        )}
                    </div>
                    {addOn.description && (
                        <p className="text-sm text-mp-muted mt-1">{addOn.description}</p>
                    )}
                </div>
                <div className="flex shrink-0 items-center justify-between gap-4 sm:justify-end">
                    <div className="text-right">
                        {isPricedLine(addOn) ? (
                            <div className="text-lg font-semibold text-mp-foreground">
                                <span>{formatCurrency(addOn.gross_price, currency)}</span>
                                <span className="ml-1 text-xs font-normal text-mp-muted">inc VAT</span>
                            </div>
                        ) : (
                            <div className="text-sm text-mp-muted">Price unavailable</div>
                        )}
                    </div>
                    <AddOnCardActions
                        addOnName={addOn.name}
                        formId={formId}
                        isAdding={isAdding}
                        canConfirm={canConfirm}
                        onStart={() => setIsAdding(true)}
                        onConfirm={() => void confirm()}
                        onCancel={closeForm}
                    />
                </div>
            </div>

            {isAdding && (
                <AddOnScheduleForm
                    id={formId}
                    addOnId={addOn.id}
                    addOnName={addOn.name}
                    control={control}
                    linkedServiceId={linkedServiceId}
                    options={options}
                />
            )}

            {instances.length > 0 && (
                <div className="mt-4 space-y-2">
                    {instances.map((instance, index) => (
                        <AddOnItem
                            key={instance.instanceId}
                            addOn={instance}
                            onRemove={onRemove}
                            serviceList={services}
                            currency={currency}
                            category={addOn.category}
                            instanceIndex={index}
                            instanceCount={instances.length}
                        />
                    ))}
                </div>
            )}
        </section>
    );
}
